import { mergeAttributes, Node } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { TEMPLATE_VARIABLES } from '@/components/template-variables-panel';
import { Badge } from '@/components/ui/badge';

// Build a lookup from variable key (e.g. "<<customerName>>") to its panel category
const variableCategoryMap = new Map<string, string>();
for (const v of TEMPLATE_VARIABLES) {
  variableCategoryMap.set(v.variable, v.category);
}

// Component to render the variable as a colored pill
// biome-ignore lint/suspicious/noExplicitAny: pre-existing
const VariableComponent = ({ node }: any) => {
  const variable = node.attrs.variable;

  // Determine category and color based on the predefined TEMPLATE_VARIABLES list
  const getCategoryStyle = (variable: string) => {
    const category = variableCategoryMap.get(variable);
    switch (category) {
      case 'Customer Information':
        return { variant: 'accent' as const, accent: 'blue-violet' as const };
      case 'Transaction Details':
        return { variant: 'accent' as const, accent: 'teal' as const };
      case 'Company Information':
        return { variant: 'accent' as const, accent: 'purple' as const };
      case 'Payment Information':
        return { variant: 'accent' as const, accent: 'orange' as const };
      case 'System Information':
        return { variant: 'accent' as const, accent: 'lagoon' as const };
      default:
        return { variant: 'secondary' as const, accent: undefined };
    }
  };

  const style = getCategoryStyle(variable);

  return (
    <NodeViewWrapper as="span" className="inline-flex items-center" data-variable={variable}>
      <Badge
        variant={style.variant}
        accent={style.accent}
        className="text-[10px] font-mono cursor-default select-none mx-0.5"
        contentEditable={false}
        draggable={false}
      >
        {variable}
      </Badge>
    </NodeViewWrapper>
  );
};

// TipTap extension for variables
export const VariableExtension = Node.create({
  name: 'variable',

  group: 'inline',

  inline: true,

  atom: true,

  // Mark as selectable but not editable
  selectable: true,

  draggable: false,

  addAttributes() {
    return {
      variable: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-variable'),
        renderHTML: (attributes) => {
          if (!attributes.variable) {
            return {};
          }
          return {
            'data-variable': attributes.variable,
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-variable]',
        getAttrs: (element: HTMLElement | string) => {
          if (typeof element === 'string') return false;
          const variable = element.getAttribute('data-variable');
          return variable ? { variable } : false;
        },
      },
    ];
  },

  // biome-ignore lint/suspicious/noExplicitAny: pre-existing
  renderHTML({ node }: any) {
    // Return span with data attribute and the variable text as content
    // This ensures the variable is visible in HTML output (for saving/preview)
    return [
      'span',
      {
        class: 'variable-node',
        'data-variable': node.attrs.variable,
      },
      node.attrs.variable,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(VariableComponent, {
      // Ensure the node view is always used
      stopEvent: () => true,
    });
  },

  addCommands() {
    return {
      insertVariable:
        (variable: string) =>
        ({
          commands,
        }: {
          commands: { insertContent: (content: { type: string; attrs: Record<string, string> }) => boolean };
        }) => {
          return commands.insertContent({
            type: this.name,
            attrs: { variable },
          });
        },
    } as unknown as Partial<import('@tiptap/core').RawCommands>;
  },
});
